import { alpha, createTheme } from '@mui/material/styles'

// Neutral slate surfaces + one indigo accent; compact density for data-heavy CRM screens
const tokens = {
  light: {
    accent: '#4f46e5',
    background: '#f6f7f9',
    paper: '#ffffff',
    border: '#e3e6eb',
    text: '#14171f',
    textMuted: '#5c6370',
    // charts (dataviz reference palette; series-1 validated on this surface)
    series1: '#2a78d6',
    grid: '#e1e0d9',
    axis: '#c3c2b7',
    axisText: '#898781',
    good: '#006300',
    bad: '#d03b3b',
  },
  dark: {
    accent: '#8b8cf6',
    background: '#0f1115',
    paper: '#171a21',
    border: '#2a2f3a',
    text: '#e8eaee',
    textMuted: '#9aa1ad',
    series1: '#3987e5',
    grid: '#2c2c2a',
    axis: '#383835',
    axisText: '#898781',
    good: '#0ca30c',
    bad: '#e66767',
  },
}

export const createAppTheme = (mode) => {
  const t = tokens[mode]
  return createTheme({
    palette: {
      mode,
      primary: { main: t.accent },
      background: { default: t.background, paper: t.paper },
      divider: t.border,
      text: { primary: t.text, secondary: t.textMuted },
      chart: { series1: t.series1, grid: t.grid, axis: t.axis, axisText: t.axisText, good: t.good, bad: t.bad },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: 13.5,
      h4: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.01em' },
      h5: { fontSize: '1.25rem', fontWeight: 600 },
      h6: { fontSize: '1rem', fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 500 },
    },
    components: {
      MuiButton: { defaultProps: { disableElevation: true, size: 'small' } },
      MuiIconButton: { defaultProps: { size: 'small' } },
      MuiTextField: { defaultProps: { size: 'small', fullWidth: true } },
      MuiFormControl: { defaultProps: { size: 'small' } },
      MuiSelect: { defaultProps: { size: 'small' } },
      MuiAutocomplete: { defaultProps: { size: 'small' } },
      MuiTable: { defaultProps: { size: 'small' } },
      // phones: dialogs use almost the whole screen (MUI's 32px margins leave little room)
      MuiDialog: {
        styleOverrides: {
          paper: {
            '@media (max-width:599.95px)': { margin: 8, width: 'calc(100% - 16px)', maxHeight: 'calc(100% - 16px)' },
          },
        },
      },
      // wide tables scroll sideways inside their box instead of stretching the page
      MuiTableContainer: { styleOverrides: { root: { overflowX: 'auto' } } },
      MuiChip: { defaultProps: { size: 'small' } },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { outlined: { borderColor: t.border } },
      },
      MuiCard: { defaultProps: { variant: 'outlined' } },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
        styleOverrides: { root: { borderBottom: `1px solid ${t.border}`, backgroundColor: t.paper } },
      },
      MuiDrawer: {
        styleOverrides: { paper: { borderRight: `1px solid ${t.border}`, backgroundColor: t.paper } },
      },
      MuiTableCell: {
        styleOverrides: {
          head: { fontWeight: 600, color: t.textMuted, backgroundColor: t.paper },
          root: { borderBottomColor: t.border },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            '&.Mui-selected': { backgroundColor: alpha(t.accent, 0.12), color: t.accent },
            '&.Mui-selected .MuiListItemIcon-root': { color: t.accent },
          },
        },
      },
    },
  })
}
